import { useEffect, useSyncExternalStore } from 'react';

/**
 * Donations since the visitor arrived: the count belongs to the visit and survives client navigation. A
 * store, not provider state, so a tick re-renders only the odometer. The count is SIMULATED; this module
 * is the only seam to swap for the real feed (`routes/old/live-counter.tsx`), which suspends and rotates
 * a five-minute window.
 */

/** Donations arrive in ones with the occasional cluster, so gap and jump are both drawn per tick. */
const MIN_GAP_MS = 600;
const MAX_GAP_MS = 3400;
const BURST_SIZES = [1, 1, 1, 1, 1, 1, 2, 2, 3, 4];
/** Also the most the odometer's three tiles can show. */
const TOTAL = 999;

function pick<T>(options: readonly T[]): T {
	return options[Math.floor(Math.random() * options.length)]!;
}

let count = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

// A self-rescheduling timeout, not an interval: the wait differs every time.
function schedule() {
	if (count >= TOTAL) return;
	timer = setTimeout(
		() => {
			count = Math.min(TOTAL, count + pick(BURST_SIZES));
			for (const listener of listeners) listener();
			schedule();
		},
		MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS),
	);
}

function subscribe(listener: () => void) {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

function getSnapshot(): number {
	return count;
}

function getServerSnapshot(): number {
	return 0;
}

/** Called from `/www`'s route component, so the count runs whichever page the visit entered through. */
export function useStartDonationCount(): void {
	useEffect(() => {
		// A mid-visit remount of the route group must not double the ticks.
		if (timer !== undefined) return;
		schedule();
	}, []);
}

export function useDonationCount(): number {
	return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
